{********************************************************************}
{                                                                    }
{ written by TMS Software                                            }
{            copyright (c) 2024                                      }
{            Email : info@tmssoftware.com                            }
{            Web : https://www.tmssoftware.com                       }
{                                                                    }
{ The source code is given as is. The author is not responsible      }
{ for any possible damage done due to the use of this code.          }
{ The complete source code remains property of the author and may    }
{ not be distributed, published, given or sold in any form as such.  }
{ No parts of the source code can be included in any other component }
{ or application without written authorization of the author.        }
{********************************************************************}

unit WEBLib.TMSFNCFindDialog;

{$I WEBLib.TMSFNCDefines.inc}

{$IFDEF CMNLIB}
{$DEFINE CMNWEBLIB}
{$ENDIF}
{$IFDEF WEBLIB}
{$DEFINE CMNWEBLIB}
{$ENDIF}

interface

uses
  Classes, WEBLib.TMSFNCCustomDialog, WEBLib.TMSFNCTypes,
  WEBLib.StdCtrls
  {$IFDEF WEBLIB}
  ,WEBLib.ExtCtrls
  {$ENDIF}
  {$IFDEF FMXLIB}
  ,FMX.Memo, FMX.ComboEdit
  {$ENDIF}
  ;

type
  TTMSFNCFindDialogOption = (fdoCaseSensitive, fdoExpression, fdoCloseIfFound,
    fdoWholeWordOnly, fdoWrapAtEndOfFile, fdoContinueToNextFile, fdoMoreEnabled,
    fdoSetMarkerEnabled, fdoNextEnabled, fdoFindEnabled, fdoCloseEnabled,
    fdoMoreExpanded, fdoDown, fdoSelection);

  TTMSFNCFindDialogVisibleOption = (fdovCaseSensitive, fdovExpression, fdovCloseIfFound,
    fdovWholeWordOnly, fdovWrapAtEndOfFile, fdovContinueToNextFile, fdovMore, fdovSetMarker, fdovNext, fdovFind,
    fdovClose, fdovFindCombo, fdovFindMemo, fdovDirection, fdovSelection);

  TTMSFNCFindDialogOptions = set of TTMSFNCFindDialogOption;
  TTMSFNCFindDialogVisibleOptions = set of TTMSFNCFindDialogVisibleOption;

const
  DefaultFindOptions = [fdoMoreEnabled, fdoNextEnabled, fdoSetMarkerEnabled, fdoFindEnabled, fdoCloseEnabled, fdoDown];
  DefaultVisibleOptions = [fdovCaseSensitive, fdovExpression, fdovCloseIfFound,
    fdovWholeWordOnly, fdovWrapAtEndOfFile, fdovNext, fdovFind,
    fdovClose, fdovFindCombo, fdovFindMemo, fdovDirection];

type
  {$IFDEF FMXLIB}
  TTMSFNCFindDialogComboBox = class(TComboEdit);
  {$ENDIF}
  {$IFDEF CMNWEBLIB}
  TTMSFNCFindDialogComboBox = class(TComboBox);
  {$ENDIF}

  TTMSFNCFindDialogUILangauge = class(TPersistent)
  private
    FFindWhat: string;
    FCloseIfFound: string;
    FLess: string;
    FCaseSensitive: string;
    FSelection: string;
    FWrapAtEndOfFile: string;
    FDirection: string;
    FSetMarker: string;
    FMore: string;
    FDown: string;
    FCaption: string;
    FNext: string;
    FWholeWordOnly: string;
    FExpression: string;
    FUp: string;
    FClose: string;
    FFind: string;
    FContinueToNextFile: string;
    FOnChanged: TNotifyEvent;
    function IsCaptionStored: Boolean;
    function IsCaseSensitiveStored: Boolean;
    function IsCloseIfFoundStored: Boolean;
    function IsCloseStored: Boolean;
    function IsContinueToNextFileStored: Boolean;
    function IsDirectionStored: Boolean;
    function IsDownStored: Boolean;
    function IsExpressionStored: Boolean;
    function IsFindStored: Boolean;
    function IsFindWhatStored: Boolean;
    function IsLessStored: Boolean;
    function IsMoreStored: Boolean;
    function IsNextStored: Boolean;
    function IsSelectionStored: Boolean;
    function IsSetMarkerStored: Boolean;
    function IsUpStored: Boolean;
    function IsWholeWordOnlyStored: Boolean;
    function IsWrapAtEndOfFileStored: Boolean;
    procedure SetCaption(const Value: string);
  protected
    procedure Changed;
  public
    constructor Create;
    procedure Assign(Source: TPersistent); override;
    property OnChanged: TNotifyEvent read FOnChanged write FOnChanged;
  published
    property CaseSensitive: string read FCaseSensitive write FCaseSensitive stored IsCaseSensitiveStored;
    property Close: string read FClose write FClose stored IsCloseStored;
    property CloseIfFound: string read FCloseIfFound write FCloseIfFound stored IsCloseIfFoundStored;
    property ContinueToNextFile: string read FContinueToNextFile write FContinueToNextFile stored IsContinueToNextFileStored;
    property Direction: string read FDirection write FDirection stored IsDirectionStored;
    property Down: string read FDown write FDown stored IsDownStored;
    property Expression: string read FExpression write FExpression stored IsExpressionStored;
    property Find: string read FFind write FFind stored IsFindStored;
    property Caption: string read FCaption write SetCaption stored IsCaptionStored;
    property FindWhat: string read FFindWhat write FFindWhat stored IsFindWhatStored;
    property Less: string read FLess write FLess stored IsLessStored;
    property More: string read FMore write FMore stored IsMoreStored;
    property Next: string read FNext write FNext stored IsNextStored;
    property Selection: string read FSelection write FSelection stored IsSelectionStored;
    property SetMarker: string read FSetMarker write FSetMarker stored IsSetMarkerStored;
    property Up: string read FUp write FUp stored IsUpStored;
    property WholeWordOnly: string read FWholeWordOnly write FWholeWordOnly stored IsWholeWordOnlyStored;
    property WrapAtEndOfFile: string read FWrapAtEndOfFile write FWrapAtEndOfFile stored IsWrapAtEndOfFileStored;
  end;

  ITMSFNCFind = interface
    ['{02B8E1DA-978E-49B0-BC84-EF2ACCB47FCF}']
    procedure FindValue(AText: string; var AFound: Boolean);
    procedure FindNextValue(AText: string; var AFound: Boolean);
    procedure FindEditChange(APrevText, ANewText: string);
    procedure SetMarker;
  end;

  TTMSFNCFindDialogFindEvent = procedure(Sender: TObject; AFindText: string; var AFound: Boolean) of object;
  TTMSFNCFindDialogFindEditChangeEvent = procedure(Sender: TObject; APrevText, ANewText: string) of object;

  TTMSFNCCustomFindDialog = class(TTMSFNCCustomDialog)
  private
    FDialogOpenedHeight, FDialogClosedHeight: Integer;
    FComboBoxFind: TTMSFNCFindDialogComboBox;
    FButtonFind: TButton;
    FButtonClose: TButton;
    FButtonNext: TButton;
    FButtonMore: TButton;
    FButtonMarker: TButton;
    FLabelFind: TLabel;
    FLabelFindMore: TLabel;
    FMemoFind: TMemo;
    FCheckBoxCase: TCheckBox;
    FCheckBoxWholeOnly: TCheckBox;
    FCheckBoxExpression: TCheckBox;
    FCheckBoxEOF: TCheckBox;
    FCheckBoxClose: TCheckBox;
    FCheckBoxContinue: TCheckBox;
    FCheckBoxSelection: TCheckBox;
    FDirGroup: TGroupBox;
    FRadioDown: TRadioButton;
    FRadioUp: TRadioButton;
    FOnFind: TTMSFNCFindDialogFindEvent;
    FOnFindNext: TTMSFNCFindDialogFindEvent;
    FOnFindEditChange: TTMSFNCFindDialogFindEditChangeEvent;
    FOnSetMarker: TNotifyEvent;
    FOptions: TTMSFNCFindDialogOptions;
    FVisibleOptions: TTMSFNCFindDialogVisibleOptions;
    FMore: Boolean;
    FUILanguage: TTMSFNCFindDialogUILangauge;
    FAutoHistory: Boolean;
    FFindText: string;
    FFindList: TStringList;
    procedure SetUILanguage(const Value: TTMSFNCFindDialogUILangauge);
  protected
    function GetInstance: NativeUInt; override;
    procedure ButtonFindClick(Sender: TObject);
    procedure ButtonNextClick(Sender: TObject);
    procedure ButtonMoreClick(Sender: TObject);
    procedure ButtonMarkerClick(Sender: TObject);
    procedure ButtonCloseClick(Sender: TObject);
    procedure CheckBoxCaseClick(Sender: TObject);
    procedure CheckBoxWholeOnlyClick(Sender: TObject);
    procedure CheckBoxExpressionClick(Sender: TObject);
    procedure CheckBoxEOFClick(Sender: TObject);
    procedure CheckBoxCloseClick(Sender: TObject);
    procedure CheckBoxContinueClick(Sender: TObject);
    procedure CheckBoxSelectionClick(Sender: TObject);
    procedure RadioClick(Sender: TObject);
    procedure ComboBoxFindChange(Sender: TObject);
    {$IFDEF FMXLIB}
    procedure ComboBoxKeyDown(Sender: TObject; var Key: Word; var KeyChar: Char; Shift: TShiftState);
    {$ENDIF}
    {$IFDEF CMNWEBLIB}
    procedure ComboBoxKeyDown(Sender: TObject; var Key: Word; Shift: TShiftState);
    {$ENDIF}
    procedure MemoFindChange(Sender: TObject);
    procedure UILanguageChanged(Sender: TObject);
    procedure UpdateMoreButton(const AValue: string);
    procedure EnableButtons;
    procedure DoFindText(AText: string);
    procedure DoFindNextText(AText: string);
    procedure DoFindEditChangeEvent(APrevText, ANewText: string);
    procedure DoSetMarkerEvent;
    procedure DoFind(AText: string; var AFound: Boolean); virtual;
    procedure DoFindNext(AText: string; var AFound: Boolean); virtual;
    procedure DoFindEditChange(APrevText, ANewText: string); virtual;
    procedure DoSetMarker; virtual;
    procedure DoClose; override;
    procedure DoFormShow(Sender: TObject); override;
    procedure BuildDialog(AParent: TTMSFNCCustomDialogParent); override;
    property AutoHistory: Boolean read FAutoHistory write FAutoHistory default True;
    property FindText: string read FFindText write FFindText;
    property FindList: TStringList read FFindList write FFindList;
    property UILanguage: TTMSFNCFindDialogUILangauge read FUILanguage write SetUILanguage;
    property Options: TTMSFNCFindDialogOptions read FOptions write FOptions default DefaultFindOptions;
    property VisibleOptions: TTMSFNCFindDialogVisibleOptions read FVisibleOptions write FVisibleOptions default DefaultVisibleOptions;
    property OnFind: TTMSFNCFindDialogFindEvent read FOnFind write FOnFind;
    property OnFindNext: TTMSFNCFindDialogFindEvent read FOnFindNext write FOnFindNext;
    property OnFindEditChange: TTMSFNCFindDialogFindEditChangeEvent read FOnFindEditChange write FOnFindEditChange;
    property OnSetMarker: TNotifyEvent read FOnSetMarker write FOnSetMarker;
  public
    constructor Create(AOwner: TComponent); override;
    destructor Destroy; override;
  end;

  {$IFNDEF LCLLIB}
  [ComponentPlatformsAttribute(TMSPlatformsWebDesktop)]
  {$ENDIF}
  TTMSFNCFindDialog = class(TTMSFNCCustomFindDialog)
  protected
    procedure RegisterRuntimeClasses; override;
  published
    property AutoHistory;
    property FindText;
    property FindList;
    property UILanguage;
    property Options;
    property VisibleOptions;
    property OnFind;
    property OnFindNext;
    property OnFindEditChange;
    property OnSetMarker;
    property OnClose;
  end;

implementation

uses
  SysUtils, Math;

{$R TMSFNCFindDialog.res}

{ TTMSFNCFindDialog }

procedure TTMSFNCFindDialog.RegisterRuntimeClasses;
begin
  inherited;
  RegisterClass(TTMSFNCFindDialog);
end;

{ TTMSFNCCustomFindDialog }

procedure TTMSFNCCustomFindDialog.BuildDialog(AParent: TTMSFNCCustomDialogParent);

  function InitCheckBox(AParentControl: TTMSFNCCustomDialogParent; ACaption: string; AChecked: Boolean; var ALeft: Integer; var ATop: Integer; var ACount: Integer): TCheckBox;
  begin
    Inc(ACount);

    Result := TCheckBox.Create(AParentControl);
    Result.Parent := AParentControl;
    Result.Width := 140;
    {$IFDEF FMXLIB}
    Result.IsChecked := AChecked;
    Result.Position.X := ALeft;
    Result.Position.Y := ATop;
    Result.Text := ACaption;
    {$ENDIF}
    {$IFDEF CMNWEBLIB}
    Result.Checked := AChecked;
    Result.Left := ALeft;
    Result.Top := ATop;
    Result.Caption := ACaption;
    {$ENDIF}
    if ALeft <> 16 then
      ALeft := 16
    else
      ALeft := 160;

    if ACount mod 2 = 0 then
      ATop := ATop + Round(Result.Height) + 6;
  end;

  function InitButton(AParentControl: TTMSFNCCustomDialogParent; ACaption: string; ALeft: Integer; var ATop: Integer): TButton;
  begin
    Result := TButton.Create(AParentControl);
    Result.Parent := AParentControl;
    Result.Width := 80;
    {$IFNDEF VCLLIB}
    Result.Height := 22;
    {$ENDIF}
    {$IFDEF VCLLIB}
    Result.Height := 25;
    {$ENDIF}
    {$IFDEF FMXLIB}
    Result.Position.X := ALeft;
    Result.Position.Y := ATop;
    Result.Text := ACaption;
    {$ENDIF}
    {$IFDEF CMNWEBLIB}
    Result.Left := ALeft;
    Result.Top := ATop;
    Result.Caption := ACaption;
    {$ENDIF}

    ATop := ATop + Round(Result.Height) + 3;
  end;

var
  tc, lc, cc, btm: Integer;
  tb: Integer;
  s: string;
begin
  inherited;
  tb := 13;
  tc := 16;
  lc := 16;
  cc := 0;

  //keep track of bottom to set the height accordingly

  FLabelFind := TLabel.Create(AParent);
  FLabelFind.Parent := AParent;
  FLabelFind.Enabled := not (fdoMoreExpanded in Options);
  {$IFDEF FMXLIB}
  FLabelFind.Text := UILanguage.FindWhat;
  FLabelFind.TextSettings.WordWrap := False;
  FLabelFind.AutoSize := True;
  FLabelFind.Position.X := 16;
  FLabelFind.Position.Y := tc;
  {$ENDIF}
  {$IFDEF CMNWEBLIB}
  FLabelFind.Left := 16;
  FLabelFind.Top := tc;
  FLabelFind.Caption := UILanguage.FindWhat;
  {$ENDIF}

  if fdovFindCombo in VisibleOptions then
  begin
    FComboBoxFind := TTMSFNCFindDialogComboBox.Create(AParent);
    FComboBoxFind.Parent := AParent;
    FComboBoxFind.Enabled := not (fdoMoreExpanded in Options);
    FComboBoxFind.TabOrder := 0;
    FComboBoxFind.Items.Assign(FindList);
    FComboBoxFind.Text := FFindText;
    FComboBoxFind.Width := 250;
    FComboBoxFind.OnChange := @ComboBoxFindChange;
    {$IFDEF FMXLIB}
    FComboBoxFind.OnKeyDown := @ComboBoxKeyDown;
    FComboBoxFind.OnChangeTracking := ComboBoxFindChange;
    FComboBoxFind.Position.X := 85;
    FComboBoxFind.Position.Y := tc - 3;
    {$ENDIF}
    {$IFDEF CMNWEBLIB}
    FComboBoxFind.OnKeyDown := @ComboBoxKeyDown;
    FComboBoxFind.Left := 85;
    FComboBoxFind.Top := tc - 3;
    {$ENDIF}
    {$IFDEF WEBLIB}
    FComboBoxFind.Style := csDropDown;
    {$ENDIF}

    tc := tc + Round(FComboBoxFind.Height) + 7;
  end;

  if fdovCaseSensitive in VisibleOptions then
  begin
    FCheckBoxCase := InitCheckBox(AParent, UILanguage.CaseSensitive, fdoCaseSensitive in Options, lc, tc, cc);
    {$IFDEF FMXLIB}
    FCheckBoxCase.OnChange := @CheckBoxCaseClick;
    {$ENDIF}
    {$IFNDEF FMXLIB}
    FCheckBoxCase.OnClick := CheckBoxCaseClick;
    {$ENDIF}
    FCheckBoxCase.TabOrder := 1;
  end;

  if fdovWholeWordOnly in VisibleOptions then
  begin
    FCheckBoxWholeOnly := InitCheckBox(AParent, UILanguage.WholeWordOnly, fdoWholeWordOnly in Options, lc, tc, cc);
    {$IFDEF FMXLIB}
    FCheckBoxWholeOnly.OnChange := @CheckBoxWholeOnlyClick;
    {$ENDIF}
    {$IFNDEF FMXLIB}
    FCheckBoxWholeOnly.OnClick := CheckBoxWholeOnlyClick;
    {$ENDIF}
    FCheckBoxWholeOnly.TabOrder := 2;
  end;

  if fdovExpression in VisibleOptions then
  begin
    FCheckBoxExpression := InitCheckBox(AParent, UILanguage.Expression, fdoExpression in Options, lc, tc, cc);
    {$IFDEF FMXLIB}
    FCheckBoxExpression.OnChange := @CheckBoxExpressionClick;
    {$ENDIF}
    {$IFNDEF FMXLIB}
    FCheckBoxExpression.OnClick := CheckBoxExpressionClick;
    {$ENDIF}
    FCheckBoxExpression.TabOrder := 3;
  end;

  if fdovWrapAtEndOfFile in VisibleOptions then
  begin
    FCheckBoxEOF := InitCheckBox(AParent, UILanguage.WrapAtEndOfFile, fdoWrapAtEndOfFile in Options, lc, tc, cc);
    {$IFDEF FMXLIB}
    FCheckBoxEOF.OnChange := @CheckBoxEOFClick;
    {$ENDIF}
    {$IFNDEF FMXLIB}
    FCheckBoxEOF.OnClick := CheckBoxEOFClick;
    {$ENDIF}
    FCheckBoxEOF.TabOrder := 4;
  end;

  if fdovCloseIfFound in VisibleOptions then
  begin
    FCheckBoxClose := InitCheckBox(AParent, UILanguage.CloseIfFound, fdoCloseIfFound in Options, lc, tc, cc);
    {$IFDEF FMXLIB}
    FCheckBoxClose.OnChange := @CheckBoxCloseClick;
    {$ENDIF}
    {$IFNDEF FMXLIB}
    FCheckBoxClose.OnClick := CheckBoxCloseClick;
    {$ENDIF}
    FCheckBoxClose.TabOrder := 5;
  end;

  if fdovContinueToNextFile in VisibleOptions then
  begin
    FCheckBoxContinue := InitCheckBox(AParent, UILanguage.ContinueToNextFile, fdoContinueToNextFile in Options, lc, tc, cc);
    {$IFDEF FMXLIB}
    FCheckBoxContinue.OnChange := @CheckBoxContinueClick;
    {$ENDIF}
    {$IFNDEF FMXLIB}
    FCheckBoxContinue.OnClick := CheckBoxContinueClick;
    {$ENDIF}
    FCheckBoxContinue.TabOrder := 6;
  end;

  if fdovSelection in VisibleOptions then
  begin
    FCheckBoxSelection := InitCheckBox(AParent, UILanguage.Selection, fdoSelection in Options, lc, tc, cc);
    {$IFDEF FMXLIB}
    FCheckBoxSelection.OnChange := @CheckBoxSelectionClick;
    {$ENDIF}
    {$IFNDEF FMXLIB}
    FCheckBoxSelection.OnClick := CheckBoxSelectionClick;
    {$ENDIF}
    FCheckBoxSelection.TabOrder := 7;
  end;

  if fdovFind in VisibleOptions then
  begin
    FButtonFind := InitButton(AParent, UILanguage.Find, 345, tb);
    FButtonFind.OnClick := ButtonFindClick;
    FButtonFind.TabOrder := 8;
  end;

  if fdovNext in VisibleOptions then
  begin
    FButtonNext := InitButton(AParent, UILanguage.Next, 345, tb);
    FButtonNext.OnClick := ButtonNextClick;
    FButtonNext.TabOrder := 9;
  end;

  if fdovSetMarker in VisibleOptions then
  begin
    FButtonMarker := InitButton(AParent, UILanguage.SetMarker, 345, tb);
    FButtonMarker.OnClick := ButtonMarkerClick;
    FButtonMarker.TabOrder := 10;
  end;

  if fdovClose in VisibleOptions then
  begin
    FButtonClose := InitButton(AParent, UILanguage.Close, 345, tb);
    FButtonClose.OnClick := ButtonCloseClick;
    FButtonClose.TabOrder := 11;
  end;

  if cc mod 2 = 1 then
    tc := tc + 26;

  btm := tc;
  {$IFDEF VCLLIB}
  tc := tc + 5;
  {$ENDIF}

  if fdovDirection in VisibleOptions then
  begin
    FDirGroup := TGroupBox.Create(AParent);
    FDirGroup.Parent := AParent;
    FDirGroup.Width := 140;
    FDirGroup.Height := 45;
    {$IFDEF FMXLIB}
    FDirGroup.Text := UILanguage.Direction;
    FDirGroup.Position.X := 16;
    FDirGroup.Position.Y := tc;
    {$ENDIF}
    {$IFDEF CMNWEBLIB}
    FDirGroup.Caption := UILanguage.Direction;
    FDirGroup.Left := 16;
    FDirGroup.Top := tc;
    {$ENDIF}

    FRadioUp := TRadioButton.Create(FDirGroup);
    FRadioUp.Parent := FDirGroup;
    FRadioUp.TabOrder := 12;
    {$IFDEF FMXLIB}
    FRadioUp.OnChange := @RadioClick;
    FRadioUp.Text := UILanguage.Up;
    FRadioUp.Position.X := 8;
    FRadioUp.Position.Y := 18;
    if not (fdoDown in Options) then
      FRadioUp.IsChecked := True;
    {$ENDIF}
    {$IFDEF CMNWEBLIB}
    FRadioUp.OnClick := RadioClick;
    FRadioUp.Caption := UILanguage.Up;
    FRadioUp.Left := 8;
    {$IFNDEF WEBLIB}
    FRadioUp.Top := 18;
    {$ENDIF}
    {$IFDEF WEBLIB}
    FRadioUp.Top := 13;
    {$ENDIF}
    if not (fdoDown in Options) then
      FRadioUp.Checked := True;
    {$ENDIF}

    FRadioDown := TRadioButton.Create(FDirGroup);
    FRadioDown.Parent := FDirGroup;
    FRadioDown.TabOrder := 13;
    {$IFDEF FMXLIB}
    FRadioDown.OnChange := @RadioClick;
    FRadioDown.Text := UILanguage.Down;
    FRadioDown.Position.X := 65;
    FRadioDown.Position.Y := 18;
    if (fdoDown in Options) then
      FRadioDown.IsChecked := True;
    {$ENDIF}
    {$IFDEF CMNWEBLIB}
    FRadioDown.OnClick := RadioClick;
    FRadioDown.Caption := UILanguage.Down;
    FRadioDown.Left := 65;
    {$IFNDEF WEBLIB}
    FRadioDown.Top := 18;
    {$ENDIF}
    {$IFDEF WEBLIB}
    FRadioDown.Top := 13;
    {$ENDIF}
    if (fdoDown in Options) then
      FRadioDown.Checked := True;
    {$ENDIF}

    btm := btm + Round(FDirGroup.Height);
    tc := tc + Round(FDirGroup.Height);
  end;

  tb := Max(tc - 25, tb);

  if fdovMore in VisibleOptions then
  begin
    if fdoMoreExpanded in Options then
      s := UILanguage.Less
    else
      s := UILanguage.More;

    FButtonMore := InitButton(AParent, s, 345, tb);
    FButtonMore.OnClick := ButtonMoreClick;
    FButtonMore.TabOrder := 14;
    btm := Max(btm, tb - 3);
  end;

  btm := btm + 10;
  FDialogClosedHeight := btm;
  FDialogOpenedHeight := btm;

  if fdovMore in VisibleOptions then
  begin
    if fdovFindMemo in VisibleOptions then
    begin
      FLabelFindMore := TLabel.Create(AParent);
      FLabelFindMore.Parent := AParent;
      FLabelFindMore.Visible := fdoMoreExpanded in Options;
      {$IFDEF FMXLIB}
      FLabelFindMore.Text := UILanguage.FindWhat;
      FLabelFindMore.TextSettings.WordWrap := False;
      FLabelFindMore.AutoSize := True;
      FLabelFindMore.Position.X := 16;
      FLabelFindMore.Position.Y := btm;
      {$ENDIF}
      {$IFDEF CMNWEBLIB}
      FLabelFindMore.Left := 16;
      FLabelFindMore.Top := btm;
      FLabelFindMore.Caption := UILanguage.FindWhat;
      {$ENDIF}

      FMemoFind := TMemo.Create(AParent);
      FMemoFind.Parent := AParent;
      FMemoFind.Visible := fdoMoreExpanded in Options;
      FMemoFind.TabOrder := 15;
      FMemoFind.Width := 410;
      FMemoFind.Height := 80;
      FMemoFind.OnChange := @MemoFindChange;
      {$IFDEF FMXLIB}
      FMemoFind.OnChangeTracking := MemoFindChange;
      FMemoFind.Position.X := 16;
      FMemoFind.Position.Y := FLabelFindMore.Position.Y + FLabelFindMore.Height + 8;
      {$ENDIF}
      {$IFDEF CMNWEBLIB}
      FMemoFind.Left := 16;
      FMemoFind.Top := FLabelFindMore.Top + FLabelFindMore.Height + 8;
      {$ENDIF}

      {$IFDEF FMXLIB}
      FDialogOpenedHeight := Round(FMemoFind.Position.Y + FMemoFind.Height + 13);
      {$ENDIF}
      {$IFDEF CMNWEBLIB}
      FDialogOpenedHeight := Round(FMemoFind.Top + FMemoFind.Height + 13);
      {$ENDIF}
    end;
  end;

  if fdoMoreExpanded in Options then
  begin
    FMore := True;
    DialogHeight := FDialogOpenedHeight;
  end
  else
  begin
    DialogHeight := FDialogClosedHeight;
  end;

  Caption := UILanguage.Caption;
  EnableButtons;
end;

procedure TTMSFNCCustomFindDialog.ButtonCloseClick(Sender: TObject);
begin
  if Assigned(Form) then
    Form.Close;
end;

procedure TTMSFNCCustomFindDialog.ButtonFindClick(Sender: TObject);
begin
  DoFindText(FFindText);
end;

procedure TTMSFNCCustomFindDialog.ButtonMarkerClick(Sender: TObject);
begin
  DoSetMarkerEvent;
end;

procedure TTMSFNCCustomFindDialog.ButtonMoreClick(Sender: TObject);
begin
  if not FMore then
  begin
    UpdateMoreButton(UILanguage.Less);
    Options := Options + [fdoMoreExpanded];
    DialogHeight := FDialogOpenedHeight;
  end
  else
  begin
    UpdateMoreButton(UILanguage.More);
    Options := Options - [fdoMoreExpanded];
    DialogHeight := FDialogClosedHeight;
  end;

  FLabelFindMore.Visible := not FMore;
  FMemoFind.Visible := not FMore;

  FLabelFind.Enabled := FMore;
  FComboBoxFind.Enabled := FMore;
  FMore := not FMore;
end;

procedure TTMSFNCCustomFindDialog.ButtonNextClick(Sender: TObject);
begin
  DoFindNextText(FFindText);
end;

procedure TTMSFNCCustomFindDialog.CheckBoxCaseClick(Sender: TObject);
begin
  {$IFDEF FMXLIB}
  if FCheckBoxCase.IsChecked then
  {$ENDIF}
  {$IFDEF CMNWEBLIB}
  if FCheckBoxCase.Checked then
  {$ENDIF}
    Options := Options + [fdoCaseSensitive]
  else
    Options := Options - [fdoCaseSensitive];
end;

procedure TTMSFNCCustomFindDialog.CheckBoxCloseClick(Sender: TObject);
begin
  {$IFDEF FMXLIB}
  if FCheckBoxClose.IsChecked then
  {$ENDIF}
  {$IFDEF CMNWEBLIB}
  if FCheckBoxClose.Checked then
  {$ENDIF}
    Options := Options + [fdoCloseIfFound]
  else
    Options := Options - [fdoCloseIfFound];
end;

procedure TTMSFNCCustomFindDialog.CheckBoxContinueClick(Sender: TObject);
begin
  {$IFDEF FMXLIB}
  if FCheckBoxContinue.IsChecked then
  {$ENDIF}
  {$IFDEF CMNWEBLIB}
  if FCheckBoxContinue.Checked then
  {$ENDIF}
    Options := Options + [fdoContinueToNextFile]
  else
    Options := Options - [fdoContinueToNextFile];
end;

procedure TTMSFNCCustomFindDialog.CheckBoxEOFClick(Sender: TObject);
begin
  {$IFDEF FMXLIB}
  if FCheckBoxEOF.IsChecked then
  {$ENDIF}
  {$IFDEF CMNWEBLIB}
  if FCheckBoxEOF.Checked then
  {$ENDIF}
    Options := Options + [fdoWrapAtEndOfFile]
  else
    Options := Options - [fdoWrapAtEndOfFile];
end;

procedure TTMSFNCCustomFindDialog.CheckBoxExpressionClick(Sender: TObject);
begin
  {$IFDEF FMXLIB}
  if FCheckBoxExpression.IsChecked then
  {$ENDIF}
  {$IFDEF CMNWEBLIB}
  if FCheckBoxExpression.Checked then
  {$ENDIF}
    Options := Options + [fdoExpression]
  else
    Options := Options - [fdoExpression];
end;

procedure TTMSFNCCustomFindDialog.CheckBoxSelectionClick(Sender: TObject);
begin
  {$IFDEF FMXLIB}
  if FCheckBoxSelection.IsChecked then
  {$ENDIF}
  {$IFDEF CMNWEBLIB}
  if FCheckBoxSelection.Checked then
  {$ENDIF}
    Options := Options + [fdoSelection]
  else
    Options := Options - [fdoSelection];
end;

procedure TTMSFNCCustomFindDialog.CheckBoxWholeOnlyClick(Sender: TObject);
begin
  {$IFDEF FMXLIB}
  if FCheckBoxWholeOnly.IsChecked then
  {$ENDIF}
  {$IFDEF CMNWEBLIB}
  if FCheckBoxWholeOnly.Checked then
  {$ENDIF}
    Options := Options + [fdoWholeWordOnly]
  else
    Options := Options - [fdoWholeWordOnly];
end;

procedure TTMSFNCCustomFindDialog.ComboBoxFindChange(Sender: TObject);
var
  s: string;
begin
  s := FComboBoxFind.Text;
  DoFindEditChangeEvent(FindText, s);
  FindText := s;
  EnableButtons;
end;

{$IFDEF FMXLIB}
procedure TTMSFNCCustomFindDialog.ComboBoxKeyDown(Sender: TObject;
  var Key: Word; var KeyChar: Char; Shift: TShiftState);
{$ENDIF}
{$IFDEF CMNWEBLIB}
procedure TTMSFNCCustomFindDialog.ComboBoxKeyDown(Sender: TObject;
  var Key: Word; Shift: TShiftState);
{$ENDIF}
begin
  if Key = KEY_RETURN then
    DoFindText(FFindText);
end;

constructor TTMSFNCCustomFindDialog.Create(AOwner: TComponent);
begin
  inherited;
  FDialogOpenedHeight := 0;
  FDialogClosedHeight := 0;
  FOptions := DefaultFindOptions;
  FVisibleOptions := DefaultVisibleOptions;
  FMore := False;
  FAutoHistory := True;
  FFindText := '';

  FUILanguage := TTMSFNCFindDialogUILangauge.Create;
  FUILanguage.OnChanged := @UILanguageChanged;
  FFindList := TStringList.Create;

  {$IFDEF WEBLIB}
  Caption := UILanguage.Caption;
  {$ENDIF}
  AllowReturnKeyClose := False;
end;

destructor TTMSFNCCustomFindDialog.Destroy;
begin
  FFindList.Free;
  FUILanguage.Free;
  inherited;
end;

procedure TTMSFNCCustomFindDialog.DoClose;
begin
  if FAutoHistory and (FFindText <> '') then
  begin
    if FFindList.IndexOf(FFindText) = -1 then
      FFindList.Add(FFindText);
  end;

  inherited;
end;

procedure TTMSFNCCustomFindDialog.DoFindText(AText: string);
var
  f: ITMSFNCFind;
  b: Boolean;
begin
  if FAutoHistory and (AText <> '') then
  begin
    if FFindList.IndexOf(AText) = -1 then
      FFindList.Add(AText);

    if fdovFindCombo in VisibleOptions then
    begin
      FComboBoxFind.Items.Assign(FFindList);
      FComboBoxFind.Text := AText;
    end;
  end;

  b := False;

  if Supports(Control, ITMSFNCFind, f) then
    f.FindValue(AText, b);

  DoFind(AText, b);

  if Assigned(Form) and Assigned(FCheckBoxClose) and {$IFDEF FMXLIB}FCheckBoxClose.IsChecked{$ENDIF}{$IFDEF CMNWEBLIB}FCheckBoxClose.Checked{$ENDIF} and b then
    Form.Close;
end;

procedure TTMSFNCCustomFindDialog.DoFormShow(Sender: TObject);
begin
  inherited;
  if Assigned(FComboBoxFind) and FComboBoxFind.Visible then
    FComboBoxFind.SetFocus;

  if Assigned(FMemoFind) and FMemoFind.Visible then
    FMemoFind.SetFocus;
end;

procedure TTMSFNCCustomFindDialog.DoFind(AText: string; var AFound: Boolean);
begin
  if Assigned(OnFind) then
    OnFind(Self, AText, AFound);
end;

procedure TTMSFNCCustomFindDialog.DoFindEditChange(APrevText, ANewText: string);
begin
  if Assigned(OnFindEditChange) then
    OnFindEditChange(Self, APrevText, ANewText);
end;

procedure TTMSFNCCustomFindDialog.DoFindEditChangeEvent(APrevText,
  ANewText: string);
var
  f: ITMSFNCFind;
begin
  if Supports(Control, ITMSFNCFind, f) then
    f.FindEditChange(APrevText, ANewText);

  DoFindEditChange(APrevText, ANewText);
end;

procedure TTMSFNCCustomFindDialog.DoFindNext(AText: string;
  var AFound: Boolean);
begin
  if Assigned(OnFind) then
    OnFindNext(Self, AText, AFound);
end;

procedure TTMSFNCCustomFindDialog.DoFindNextText(AText: string);
var
  f: ITMSFNCFind;
  b: Boolean;
begin
  if FAutoHistory and (AText <> '') then
  begin
    if FFindList.IndexOf(AText) = -1 then
      FFindList.Add(AText);

    if fdovFindCombo in VisibleOptions then
    begin
      FComboBoxFind.Items.Assign(FFindList);
      FComboBoxFind.Text := AText;
    end;
  end;

  b := False;
  if Supports(Control, ITMSFNCFind, f) then
    f.FindNextValue(AText, b);

  DoFindNext(AText, b);

  if Assigned(Form) and Assigned(FCheckBoxClose) and {$IFDEF FMXLIB}FCheckBoxClose.IsChecked{$ENDIF}{$IFDEF CMNWEBLIB}FCheckBoxClose.Checked{$ENDIF} and b then
    Form.Close;
end;

procedure TTMSFNCCustomFindDialog.DoSetMarker;
begin
  if Assigned(OnSetMarker) then
    OnSetMarker(Self);
end;

procedure TTMSFNCCustomFindDialog.DoSetMarkerEvent;
var
  f: ITMSFNCFind;
begin
  if Supports(Control, ITMSFNCFind, f) then
    f.SetMarker;

  DoSetMarker;
end;

procedure TTMSFNCCustomFindDialog.EnableButtons;
begin
  if fdovFind in VisibleOptions then
    FButtonFind.Enabled := (FindText <> '') and (fdoFindEnabled in Options);

  if fdovNext in VisibleOptions then
    FButtonNext.Enabled := (FindText <> '') and (fdoNextEnabled in Options);

  if fdovSetMarker in VisibleOptions then
    FButtonMarker.Enabled := (FindText <> '') and (fdoSetMarkerEnabled in Options);
end;

function TTMSFNCCustomFindDialog.GetInstance: NativeUInt;
begin
  Result := HInstance;
end;

procedure TTMSFNCCustomFindDialog.MemoFindChange(Sender: TObject);
var
  s: string;
  i: Integer;
begin
  s := '';
  for i := 0 to FMemoFind.Lines.Count - 1 do
  begin
    if i > 0 then
      s := s + #13#10 + FMemoFind.Lines[i]
    else
      s := FMemoFind.Lines[i];
  end;

  DoFindEditChangeEvent(FindText, s);
  FindText := s;
  EnableButtons;
end;

procedure TTMSFNCCustomFindDialog.RadioClick(Sender: TObject);
begin
  {$IFDEF FMXLIB}
  if FRadioDown.IsChecked then
  {$ENDIF}
  {$IFDEF CMNWEBLIB}
  if FRadioDown.Checked then
  {$ENDIF}
    Options := Options + [fdoDown]
  else
    Options := Options - [fdoDown];
end;

procedure TTMSFNCCustomFindDialog.SetUILanguage(
  const Value: TTMSFNCFindDialogUILangauge);
begin
  FUILanguage.Assign(Value);
end;

procedure TTMSFNCCustomFindDialog.UILanguageChanged(Sender: TObject);
begin
  {$IFDEF WEBLIB}
  Caption := UILanguage.Caption;
  {$ENDIF}
end;

procedure TTMSFNCCustomFindDialog.UpdateMoreButton(const AValue: string);
begin
  if Assigned(FButtonMore) then
  begin
    {$IFDEF FMXLIB}
    FButtonMore.Text := AValue;
    {$ENDIF}
    {$IFDEF CMNWEBLIB}
    FButtonMore.Caption := AValue;
    {$ENDIF}
  end;
end;

{ TTMSFNCFindDialogUILangauge }

procedure TTMSFNCFindDialogUILangauge.Assign(Source: TPersistent);
begin
  if Source is TTMSFNCFindDialogUILangauge then
  begin
    FCaseSensitive := (Source as TTMSFNCFindDialogUILangauge).CaseSensitive;
    FClose := (Source as TTMSFNCFindDialogUILangauge).Close;
    FCloseIfFound := (Source as TTMSFNCFindDialogUILangauge).CloseIfFound;
    FContinueToNextFile := (Source as TTMSFNCFindDialogUILangauge).ContinueToNextFile;
    FDirection := (Source as TTMSFNCFindDialogUILangauge).Direction;
    FDown := (Source as TTMSFNCFindDialogUILangauge).Down;
    FExpression := (Source as TTMSFNCFindDialogUILangauge).Expression;
    FFind := (Source as TTMSFNCFindDialogUILangauge).Find;
    FCaption := (Source as TTMSFNCFindDialogUILangauge).Caption;
    FFindWhat := (Source as TTMSFNCFindDialogUILangauge).FindWhat;
    FLess := (Source as TTMSFNCFindDialogUILangauge).Less;
    FMore := (Source as TTMSFNCFindDialogUILangauge).More;
    FNext := (Source as TTMSFNCFindDialogUILangauge).Next;
    FSelection := (Source as TTMSFNCFindDialogUILangauge).Selection;
    FSetMarker := (Source as TTMSFNCFindDialogUILangauge).SetMarker;
    FUp := (Source as TTMSFNCFindDialogUILangauge).Up;
    FWholeWordOnly := (Source as TTMSFNCFindDialogUILangauge).WholeWordOnly;
    FWrapAtEndOfFile := (Source as TTMSFNCFindDialogUILangauge).WrapAtEndOfFile;
  end
  else
    inherited;
end;

procedure TTMSFNCFindDialogUILangauge.Changed;
begin
  if Assigned(OnChanged) then
    OnChanged(Self);
end;

constructor TTMSFNCFindDialogUILangauge.Create;
begin
  FCaseSensitive := 'Case sensitive';
  FClose := 'Close';
  FCloseIfFound := 'Close if found';
  FContinueToNextFile := 'Continue to next file';
  FDirection := 'Direction';
  FDown := 'Down';
  FExpression := 'Expression';
  FFind := 'Find';
  FCaption := 'Find';
  FFindWhat := 'Find what:';
  FLess := 'Less';
  FMore := 'More';
  FNext := 'Next';
  FSelection := 'Selection';
  FSetMarker := 'Set marker';
  FUp := 'Up';
  FWholeWordOnly := 'Whole word only';
  FWrapAtEndOfFile := 'Wrap at end of file';
end;

function TTMSFNCFindDialogUILangauge.IsCaptionStored: Boolean;
begin
  Result := FCaption <> 'Find';
end;

function TTMSFNCFindDialogUILangauge.IsCaseSensitiveStored: Boolean;
begin
  Result := FCaseSensitive <> 'Case sensitive';
end;

function TTMSFNCFindDialogUILangauge.IsCloseIfFoundStored: Boolean;
begin
  Result := FCloseIfFound <> 'Close if found';
end;

function TTMSFNCFindDialogUILangauge.IsCloseStored: Boolean;
begin
  Result := FClose <> 'Close';
end;

function TTMSFNCFindDialogUILangauge.IsContinueToNextFileStored: Boolean;
begin
  Result := FContinueToNextFile <> 'Continue to next file'
end;

function TTMSFNCFindDialogUILangauge.IsDirectionStored: Boolean;
begin
  Result := FDirection <> 'Direction';
end;

function TTMSFNCFindDialogUILangauge.IsDownStored: Boolean;
begin
  Result := FDown <> 'Down';
end;

function TTMSFNCFindDialogUILangauge.IsExpressionStored: Boolean;
begin
  Result := FExpression <> 'Expression';
end;

function TTMSFNCFindDialogUILangauge.IsFindStored: Boolean;
begin
  Result := FFind <> 'Find';
end;

function TTMSFNCFindDialogUILangauge.IsFindWhatStored: Boolean;
begin
  Result := FFindWhat <> 'Find what:';
end;

function TTMSFNCFindDialogUILangauge.IsLessStored: Boolean;
begin
  Result := FLess <> 'Less';
end;

function TTMSFNCFindDialogUILangauge.IsMoreStored: Boolean;
begin
  Result := FMore <> 'More';
end;

function TTMSFNCFindDialogUILangauge.IsNextStored: Boolean;
begin
  Result := FNext <> 'Next';
end;

function TTMSFNCFindDialogUILangauge.IsSelectionStored: Boolean;
begin
  Result := FSelection <> 'Selection';
end;

function TTMSFNCFindDialogUILangauge.IsSetMarkerStored: Boolean;
begin
  Result := FSetMarker <> 'Set marker';
end;

function TTMSFNCFindDialogUILangauge.IsUpStored: Boolean;
begin
  Result := FUp <> 'Up';
end;

function TTMSFNCFindDialogUILangauge.IsWholeWordOnlyStored: Boolean;
begin
  Result := FWholeWordOnly <> 'Whole word only';
end;

function TTMSFNCFindDialogUILangauge.IsWrapAtEndOfFileStored: Boolean;
begin
  Result := FWrapAtEndOfFile <> 'Wrap at end of file';
end;

procedure TTMSFNCFindDialogUILangauge.SetCaption(const Value: string);
begin
  if FCaption <> Value then
  begin
    FCaption := Value;
    Changed;
  end;
end;

end.
